import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  logInService,
  requestOtpLoginService,
  verifyOtpLoginService,
} from "../api/services";
import { PasswordInput } from "../components/PasswordInput";
import { useToast } from "../components/Toast";
import { useRequestError } from "../hooks/useRequestError";
import { useAuthStore } from "../stores/authStore";
import { isValidOtpCode, isValidSaudiPhone } from "../utils/otpValidation";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();
  const { setSessionFromToken, isAuthenticated, hydrated } = useAuthStore();

  const [authMode, setAuthMode] = useState<"password" | "otp">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const isValidPhone = useMemo(
    () => isValidSaudiPhone(phoneNumber),
    [phoneNumber]
  );
  const isValidOtp = useMemo(() => isValidOtpCode(otpCode), [otpCode]);

  useEffect(() => {
    if (hydrated && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [hydrated, isAuthenticated, navigate]);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setCooldownSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSeconds]);

  const goToDashboard = () => {
    const ok = setSessionFromToken();
    if (!ok) {
      showToast({
        type: "error",
        title: "Access denied",
        message: "This dashboard is only available for doctors and caregivers.",
      });
      return;
    }
    navigate("/", { replace: true });
  };

  const onPasswordLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      return;
    }
    setSubmitting(true);
    try {
      await logInService({ email: email.trim(), password });
      goToDashboard();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401) {
        showToast({
          type: "error",
          title: "Login Failed",
          message:
            "The email or password you entered is incorrect. Please try again.",
        });
      } else {
        handleRequestError(error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onRequestOtp = async () => {
    if (!isValidPhone || cooldownSeconds > 0) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await requestOtpLoginService({ phone: phoneNumber });
      setOtpRequested(true);
      setCooldownSeconds(response.cooldown_seconds || 60);
      showToast({
        type: "success",
        title: "OTP Sent",
        message: response.message,
      });
    } catch (error) {
      handleRequestError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    if (!isValidPhone || !isValidOtp) {
      return;
    }
    setSubmitting(true);
    try {
      await verifyOtpLoginService({
        phone: phoneNumber,
        otp_code: otpCode.trim(),
      });
      goToDashboard();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401) {
        showToast({
          type: "error",
          title: "OTP Verification Failed",
          message: "Invalid or expired OTP. Please try again.",
        });
      } else {
        handleRequestError(error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (mode: "password" | "otp") => {
    setAuthMode(mode);
    setOtpRequested(false);
    setOtpCode("");
    setCooldownSeconds(0);
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="REEVA" className="auth-logo" />
        <h1>Login to Your Account</h1>

        <div className="segmented">
          <button
            type="button"
            className={authMode === "password" ? "active" : ""}
            onClick={() => switchMode("password")}
          >
            Email
          </button>
          <button
            type="button"
            className={authMode === "otp" ? "active" : ""}
            onClick={() => switchMode("otp")}
          >
            Phone
          </button>
        </div>

        {authMode === "password" ? (
          <form className="stack gap-md" onSubmit={onPasswordLogin}>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label className="field">
              <span>Password</span>
              <PasswordInput
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                show={showPassword}
                onToggleShow={() => setShowPassword((v) => !v)}
                required
              />
            </label>
            <div className="row-between">
              <Link to="/forgot-password">Forgot password?</Link>
              <Link to="/signup">Sign up</Link>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : (
          <form className="stack gap-md" onSubmit={onVerifyOtp}>
            <label className="field">
              <span>Mobile number</span>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="9665xxxxxxxx"
                required
              />
            </label>
            {otpRequested ? (
              <label className="field">
                <span>OTP code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                />
              </label>
            ) : null}
            <div className="stack gap-sm">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!isValidPhone || cooldownSeconds > 0 || submitting}
                onClick={onRequestOtp}
              >
                {cooldownSeconds > 0
                  ? `Resend in ${cooldownSeconds}s`
                  : otpRequested
                    ? "Resend OTP"
                    : "Send OTP"}
              </button>
              {otpRequested ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isValidPhone || !isValidOtp || submitting}
                >
                  {submitting ? "Verifying…" : "Verify & sign in"}
                </button>
              ) : null}
            </div>
            <p className="center muted">
              New here? <Link to="/signup">Create an account</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};
